import { memo, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import get from 'lodash/get';
import capitalize from 'lodash/capitalize';

import { Slider } from '../../../Slider/Slider';
import { AssetVertical } from '../../../Asset/AssetVertical/AssetVertical';
import {
  Placeholder,
  placeholderTypes,
} from '../../../Placeholder/Placeholder';
import { usePlaceholders } from '../../usePlaceholders';
import * as staylive from '../../../../services/staylive';
import { useServerTime } from '../../../utils/useServerTime';
import { Badge } from '../../../Asset/shared/Badge/Badge';
import {
  formatCalendarTime,
  getUnixTimestamp,
} from '../../../../utils/datetime';

import type { SvpAssetAdditional } from '@vgtv/api-client/lib/svp_asset';
import type { StayLiveSliderComponent } from '@vgtv/api-client/lib/component/slider';
import type { FC } from 'react';
import type { StayliveStream } from '../../../../services/staylive';
import type { ImageSources } from '../../../../helpers/images';

import * as styles from './StayliveStreamsSlider.module.scss';

type Props = {
  componentConfig: StayLiveSliderComponent;
};

const StayliveStreamsSliderBase: FC<Props> = ({ componentConfig }) => {
  const [isFetched, setIsFetched] = useState(false);
  const [streams, setStreams] = useState<StayliveStream[]>([]);
  const { serverTime } = useServerTime();

  const fetchStreams = useCallback(async () => {
    const streams = await staylive.fetchStreams(componentConfig.data.url!);

    setStreams(streams);
    setIsFetched(true);
  }, [componentConfig]);

  useEffect(() => {
    fetchStreams();
  }, [fetchStreams]);

  const { t } = useTranslation();

  const { header } = componentConfig;

  const channelId = get(componentConfig, 'data.source.params.channelId');

  const placeholders = usePlaceholders(isFetched);

  if (isFetched && streams.length === 0) {
    return null;
  }

  const headerButtonLink = staylive.getChannelUrl(channelId!);
  const hasHeaderButton = headerButtonLink !== undefined;
  const headerButtonLabel = t('see all');
  const onHeaderButtonClick = () => {
    if (headerButtonLink) {
      window.location.href = headerButtonLink;
    }
  };

  return (
    <Slider
      header={header}
      headerButtonLabel={hasHeaderButton ? headerButtonLabel : undefined}
      onHeaderButtonClick={hasHeaderButton ? onHeaderButtonClick : undefined}
      className={styles.slider}
    >
      {[
        ...placeholders.map((item, index) => (
          <Placeholder key={index} type={placeholderTypes.assetVertical} />
        )),
        ...streams.map((stream) => {
          const imageSources: ImageSources = {
            src: `https://image.proxy.staylive.tv/500x312/${stream.thumbnail}`,
            aspectRatio: 'horizontal',
          };

          let badge;

          if (staylive.isStreamUpcoming(serverTime, stream)) {
            const unixStartTime = getUnixTimestamp(stream.start);

            badge = capitalize(formatCalendarTime(unixStartTime, serverTime));
          }

          if (staylive.isStreamLive(serverTime, stream)) {
            badge = <Badge label={t('Live now')} kind="live" />;
          }

          return (
            <AssetVertical
              key={stream.id}
              id={stream.id as SvpAssetAdditional['id']}
              title={stream.name}
              imageSources={imageSources}
              href={staylive.getLivestreamUrl(stream)}
              badge={badge}
            />
          );
        }),
      ]}
    </Slider>
  );
};

export const StayliveStreamsSlider = memo(StayliveStreamsSliderBase);
